import { type FC, useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "@/shared/hooks";
import type { ChatMessageListItemType } from "./types";

interface ChatMessageListItemProps {
  message: ChatMessageListItemType;
  viewerIsModerator: boolean;
  onReply: (message: ChatMessageListItemType) => void;
  // Клик по аватарке ставит упоминание автора (референсное поведение,
  // отдельной кнопки упоминания у поля ввода нет).
  onMention: (mention: { userId: string; displayName: string }) => void;
  onDelete: (id: string) => void;
  onBan: (messageId: string, type: "TEMPORARY" | "PERMANENT") => void;
}

export const ChatMessageListItem: FC<ChatMessageListItemProps> = ({
  message,
  viewerIsModerator,
  onReply,
  onMention,
  onDelete,
  onBan,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (message.messageType === "SYSTEM") {
    return (
      <Box sx={{ textAlign: "center", py: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {message.userDisplayName} {t("stream.chat.userJoined")}
        </Typography>
      </Box>
    );
  }

  const canModerate = viewerIsModerator && Boolean(message.userId);

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        // Не только hover, но и focus-within: иначе действия модератора
        // недостижимы с клавиатуры. И именно opacity вместо visibility:
        // visibility: hidden убирает кнопки из порядка обхода Tab, так что
        // focus-within на них не сработал бы никогда. pointer-events
        // сохраняет прежнее поведение мышью: пока строка не под курсором,
        // невидимые кнопки не ловят клики.
        "& .chat-message-actions": { opacity: 0, pointerEvents: "none" },
        "&:hover .chat-message-actions, &:focus-within .chat-message-actions": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      <ListItemAvatar sx={{ minWidth: 40 }}>
        <Avatar
          src={message.userAvatar?.url ?? undefined}
          sx={{
            width: 24,
            height: 24,
            cursor: message.userId ? "pointer" : undefined,
          }}
          onClick={
            message.userId
              ? () =>
                  onMention({
                    userId: String(message.userId),
                    displayName: message.userDisplayName ?? "",
                  })
              : undefined
          }
        />
      </ListItemAvatar>
      <ListItemText
        sx={{ wordBreak: "break-word" }}
        primary={
          <Typography component="span" variant="body2" color="text.secondary">
            {message.userDisplayName}
          </Typography>
        }
        secondary={
          <>
            {message.replyToMessage && (
              <Box
                sx={{
                  borderLeft: 2,
                  borderColor: "primary.main",
                  pl: 1,
                  mb: 0.5,
                  opacity: 0.8,
                }}
              >
                <Typography variant="caption" noWrap component="div">
                  {message.replyToMessage.userDisplayName}:{" "}
                  {message.replyToMessage.message}
                </Typography>
              </Box>
            )}
            {message.mentionUser && (
              <Typography
                component="span"
                variant="body2"
                sx={{ color: "primary.main", fontWeight: 500, mr: 0.5 }}
              >
                @{message.mentionUser.name}
              </Typography>
            )}
            <Typography component="span" variant="body2">
              {message.message}
            </Typography>
          </>
        }
      />
      <Box className="chat-message-actions" sx={{ display: "flex" }}>
        <IconButton
          size="small"
          aria-label={t("stream.chat.moderator.reply")}
          onClick={() => onReply(message)}
        >
          <ReplyIcon fontSize="small" />
        </IconButton>
        {canModerate && (
          <>
            <IconButton
              size="small"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete(String(message.id));
                }}
              >
                {t("stream.chat.moderator.delete")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onBan(String(message.id), "TEMPORARY");
                }}
              >
                {t("stream.chat.moderator.banTemporary")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onBan(String(message.id), "PERMANENT");
                }}
              >
                {t("stream.chat.moderator.banPermanent")}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </ListItem>
  );
};
